import React, { Fragment, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
} from '@tanstack/react-table';
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from '@headlessui/react';
import { useNavigate } from '@tanstack/react-router';
import { DeleteAnnouncementModal } from './modals';
import { useDeleteResidentAnnouncement } from '../hooks/useAnnouncementMutations';
import type { Announcement } from '../api';
import { formattedDate } from '@/shared/utils/formatDate';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react';

interface AnnouncementDataTableProps {
  data: Announcement[];
  // columns?: ColumnDef<Announcement, any>[];
  isLoading?: boolean;
  variant?: 'resident' | 'admin'; // ✅ key difference
  className?: string;
}

export const AnnouncementDataTable: React.FC<AnnouncementDataTableProps> = ({
  data,
  // columns = announcementColumns,
  variant = 'admin',
  isLoading,
  className = '',
}) => {
  // Pick the column set dynamically
  const columns =
    variant === 'resident' ? residentDashboardColumns : adminDashboardColumns;

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading)
    return (
      <div className="p-4 space-y-2">
        {[...Array(10)].map((_, i) => (
          <div key={i} className="h-10 bg-gray-300 animate-pulse rounded" />
        ))}
      </div>
    );

  return (
    <div
      className={`min-w-3xl ${className}`}
      style={{ scrollbarWidth: 'none' }}
    >
      <table className="w-full">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id} className="p-2 md:p-4 text-sm md:text-base text-center border">
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="p-2 md:p-4 text-sm md:text-base text-start border">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={columns.length}
                className="py-6 text-start text-gray-400"
              >
                <div className="text-center">
                  No announcements found.
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

// ✅ Admin Columns
const adminDashboardColumns: ColumnDef<Announcement>[] = [
  {
    accessorKey: 'title',
    header: () => <div className="text-center font-bold">Title</div>,
    cell: (info) => <div className="">{String(info.getValue() || '-')}</div>,
  },
  {
    accessorKey: 'description',
    header: () => <div className="text-center font-bold">Details</div>,
    cell: (info) => (
      <div className=" text-gray-700 truncate max-w-xs">
        {String(info.getValue() || '-')}
      </div>
    ),
  },
  {
    accessorKey: 'audience',
    header: () => <div className="text-center font-bold">Audience</div>,
    cell: (info) => (
      <div className=" text-center">{String(info.getValue() || '-')}</div>
    ),
  },
  {
    accessorKey: 'status',
    header: () => <div className="text-center font-bold">Status</div>,
    cell: (info) => {
      const status = info.getValue() as Announcement['status'];
      const color =
        status === 'PUBLISHED'
          ? 'text-green-700'
          : status === 'SCHEDULED'
          ? 'text-yellow-700'
          : status === 'EXPIRED'
          ? 'text-red-700'
          : 'text-gray-700';

      return (
        <div className="text-center">
          <span className={` ${color}`}>{status}</span>
        </div>
      );
    },
  },
  {
    accessorKey: 'postedBy.name',
    header: () => <div className="text-center font-bold">Posted by</div>,
    cell: (info) => (
      <div className=" text-center">{String(info.getValue() || '-')}</div>
    ),
  },
  {
    accessorKey: 'createdAt',
    header: () => <div className="text-center font-bold">Date & Time</div>,
    cell: (info) => (
      <div className=" text-center">
        {new Date(info.getValue() as string).toLocaleString()}
      </div>
    ),
  },
  {
    id: 'actions',
    header: () => <div className="font-bold text-center">Actions</div>,
    cell: ({ row }) => <AdminActionsCell announcement={row.original} />,
  },
];

// ✅ ActionsCell component to safely use hooks
const AdminActionsCell: React.FC<{ announcement: Announcement }> = ({
  announcement,
}) => {
  const navigate = useNavigate();

  const handleView = () =>
    navigate({ to: `/announcements/${announcement.id}` });

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  useState(false);

  /** -----------------------------
   *   Floating UI positioning
   *  ----------------------------- */
  const { refs, floatingStyles } = useFloating({
    middleware: [offset(8), flip(), shift()],
    whileElementsMounted: autoUpdate,
    placement: 'bottom-end',
  });

  return (
    <div className="flex justify-center">
      <Menu as="div" className="relative inline-block text-left">
        <MenuButton
          className="text-gray-500 hover:text-gray-700 cursor-pointer w-5"
          ref={refs.setReference}
        >
          <svg
            width="4"
            className="mx-auto"
            height="16"
            viewBox="0 0 4 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2 4C0.89543 4 -4.82823e-08 3.10457 0 2C4.82823e-08 0.89543 0.895431 -4.82823e-08 2 0C3.10457 4.82823e-08 4 0.895431 4 2C4 3.10457 3.10457 4 2 4Z"
              fill="#111827"
            />
            <path
              d="M2 10C0.89543 10 -4.82823e-08 9.10457 0 8C4.82823e-08 6.89543 0.895431 6 2 6C3.10457 6 4 6.89543 4 8C4 9.10457 3.10457 10 2 10Z"
              fill="#111827"
            />
            <path
              d="M2 16C0.89543 16 -4.82823e-08 15.1046 0 14C4.82823e-08 12.8954 0.895431 12 2 12C3.10457 12 4 12.8954 4 14C4 15.1046 3.10457 16 2 16Z"
              fill="#111827"
            />
          </svg>
        </MenuButton>

        <Transition
          as={Fragment}
          enter="transition duration-100 ease-out"
          enterFrom="transform scale-95 opacity-0"
          enterTo="transform scale-100 opacity-100"
          leave="transition duration-75 ease-in"
          leaveFrom="transform scale-100 opacity-100"
          leaveTo="transform scale-95 opacity-0"
        >
          <MenuItems
            ref={refs.setFloating}
            style={floatingStyles}
            className="absolute right-0 mt-2 w-60 origin-top-right bg-white border border-gray-200 shadow-lg focus:outline-none z-50"
          >
            <div className="py-1">
              <MenuItem>
                {() => (
                  <button
                    onClick={handleView}
                    className={`w-full text-left p-4 text-text-primary flex items-center gap-4 cursor-pointer hover:bg-gray-200 `}
                  >
                    <svg
                      width="22"
                      height="15"
                      viewBox="0 0 22 15"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11 12C12.25 12 13.3125 11.5625 14.1875 10.6875C15.0625 9.8125 15.5 8.75 15.5 7.5C15.5 6.25 15.0625 5.1875 14.1875 4.3125C13.3125 3.4375 12.25 3 11 3C9.75 3 8.6875 3.4375 7.8125 4.3125C6.9375 5.1875 6.5 6.25 6.5 7.5C6.5 8.75 6.9375 9.8125 7.8125 10.6875C8.6875 11.5625 9.75 12 11 12ZM11 10.2C10.25 10.2 9.6125 9.9375 9.0875 9.4125C8.5625 8.8875 8.3 8.25 8.3 7.5C8.3 6.75 8.5625 6.1125 9.0875 5.5875C9.6125 5.0625 10.25 4.8 11 4.8C11.75 4.8 12.3875 5.0625 12.9125 5.5875C13.4375 6.1125 13.7 6.75 13.7 7.5C13.7 8.25 13.4375 8.8875 12.9125 9.4125C12.3875 9.9375 11.75 10.2 11 10.2ZM11 15C8.56667 15 6.35 14.3208 4.35 12.9625C2.35 11.6042 0.9 9.78333 0 7.5C0.9 5.21667 2.35 3.39583 4.35 2.0375C6.35 0.679167 8.56667 0 11 0C13.4333 0 15.65 0.679167 17.65 2.0375C19.65 3.39583 21.1 5.21667 22 7.5C21.1 9.78333 19.65 11.6042 17.65 12.9625C15.65 14.3208 13.4333 15 11 15ZM11 13C12.8833 13 14.6125 12.5042 16.1875 11.5125C17.7625 10.5208 18.9667 9.18333 19.8 7.5C18.9667 5.81667 17.7625 4.47917 16.1875 3.4875C14.6125 2.49583 12.8833 2 11 2C9.11667 2 7.3875 2.49583 5.8125 3.4875C4.2375 4.47917 3.03333 5.81667 2.2 7.5C3.03333 9.18333 4.2375 10.5208 5.8125 11.5125C7.3875 12.5042 9.11667 13 11 13Z"
                        fill="#474747"
                      />
                    </svg>
                    <span>View Details</span>
                  </button>
                )}
              </MenuItem>
              <MenuItem>
                {() => (
                  <button
                    onClick={() => setIsDeleteModalOpen(true)}
                    className={`w-full text-left p-4 text-text-primary flex items-center gap-4 cursor-pointer hover:bg-gray-200 `}
                  >
                    <svg
                      width="16"
                      height="18"
                      viewBox="0 0 16 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3 18C2.45 18 1.97917 17.8042 1.5875 17.4125C1.19583 17.0208 1 16.55 1 16V3H0V1H5V0H11V1H16V3H15V16C15 16.55 14.8042 17.0208 14.4125 17.4125C14.0208 17.8042 13.55 18 13 18H3ZM13 3H3V16H13V3ZM5 14H7V5H5V14ZM9 14H11V5H9V14Z"
                        fill="#E03A3D"
                      />
                    </svg>

                    <span>Delete Annoucement</span>
                  </button>
                )}
              </MenuItem>
            </div>
          </MenuItems>
        </Transition>
      </Menu>

      {isDeleteModalOpen && (
        <DeleteAnnouncementModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={(id) => {
            // Handle delete confirmation
            console.log('Delete annoucement:', id);
            setIsDeleteModalOpen(false);
          }}
          announcement={announcement}
        />
      )}
    </div>
  );
};

// ✅ Resident Columns
const residentDashboardColumns: ColumnDef<Announcement>[] = [
  {
    accessorKey: 'title',
    header: () => <div className="text-center font-bold">Title</div>,
    cell: (info) => <div className="">{String(info.getValue() || '-')}</div>,
  },
  {
    accessorKey: 'details',
    header: () => <div className="text-center font-bold">Details</div>,
    cell: (info) => (
      <div className=" text-gray-700 truncate max-w-xs">
        {String(info.getValue() || '-')}
      </div>
    ),
  },
  {
    accessorKey: 'status',
    header: () => <div className="text-center font-bold">Status</div>,
    cell: (info) => {
      const status = info.getValue() as Announcement['status'];
      console.log('status', status);
      const color =
        status?.toUpperCase() === 'PUBLISHED'
          ? 'text-green-700'
          : status?.toUpperCase() === 'SCHEDULED'
          ? 'text-yellow-700'
          : status?.toUpperCase() === 'EXPIRED'
          ? 'text-red-700'
          : 'text-gray-700';

      return (
        <div className="text-center">
          <span className={` ${color}`}>{status}</span>
        </div>
      );
    },
  },
  {
    accessorKey: 'dateTime',
    header: () => <div className="text-center font-bold">Date & Time</div>,
    cell: (info) => (
      <div className=" text-center">
        {formattedDate(info.getValue() as string)}
      </div>
    ),
  },
  {
    id: 'actions',
    header: () => <div className="font-bold text-center">Actions</div>,
    cell: ({ row }) => <ResidentActionsCell announcement={row.original} />,
  },
];

// ✅ ActionsCell component to safely use hooks
const ResidentActionsCell: React.FC<{ announcement: Announcement }> = ({
  announcement,
}) => {
  const navigate = useNavigate();

  /** -----------------------------
   *   Floating UI positioning
   *  ----------------------------- */
  const { refs, floatingStyles } = useFloating({
    middleware: [offset(8), flip(), shift()],
    whileElementsMounted: autoUpdate,
    placement: 'bottom-end',
  });

  const handleView = () =>
    navigate({ to: `/resident/dashboard/announcements/${announcement.id}` });

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  useState(false);

  const mutation = useDeleteResidentAnnouncement();

  const handleDelete = (id: string) => {
    mutation.mutate(id);
    navigate({ to: '/resident/dashboard/announcements' });
  };

  return (
    <div className="flex justify-center">
      <Menu as="div" className="relative inline-block text-left">
        <MenuButton
          className="text-gray-500 hover:text-gray-700 cursor-pointer w-5"
          ref={refs.setReference}
        >
          <svg
            width="4"
            className="mx-auto"
            height="16"
            viewBox="0 0 4 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2 4C0.89543 4 -4.82823e-08 3.10457 0 2C4.82823e-08 0.89543 0.895431 -4.82823e-08 2 0C3.10457 4.82823e-08 4 0.895431 4 2C4 3.10457 3.10457 4 2 4Z"
              fill="#111827"
            />
            <path
              d="M2 10C0.89543 10 -4.82823e-08 9.10457 0 8C4.82823e-08 6.89543 0.895431 6 2 6C3.10457 6 4 6.89543 4 8C4 9.10457 3.10457 10 2 10Z"
              fill="#111827"
            />
            <path
              d="M2 16C0.89543 16 -4.82823e-08 15.1046 0 14C4.82823e-08 12.8954 0.895431 12 2 12C3.10457 12 4 12.8954 4 14C4 15.1046 3.10457 16 2 16Z"
              fill="#111827"
            />
          </svg>
        </MenuButton>

        <Transition
          as={Fragment}
          enter="transition duration-100 ease-out"
          enterFrom="transform scale-95 opacity-0"
          enterTo="transform scale-100 opacity-100"
          leave="transition duration-75 ease-in"
          leaveFrom="transform scale-100 opacity-100"
          leaveTo="transform scale-95 opacity-0"
        >
          <MenuItems
            ref={refs.setFloating}
            style={floatingStyles}
            className="absolute right-0 mt-2 w-60 origin-top-right bg-white border border-gray-200 shadow-lg focus:outline-none z-50"
          >
            <div className="py-1">
              <MenuItem>
                {() => (
                  <button
                    onClick={handleView}
                    className={`w-full text-left p-4 text-text-primary flex items-center gap-4 cursor-pointer hover:bg-gray-200 `}
                  >
                    <svg
                      width="22"
                      height="15"
                      viewBox="0 0 22 15"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11 12C12.25 12 13.3125 11.5625 14.1875 10.6875C15.0625 9.8125 15.5 8.75 15.5 7.5C15.5 6.25 15.0625 5.1875 14.1875 4.3125C13.3125 3.4375 12.25 3 11 3C9.75 3 8.6875 3.4375 7.8125 4.3125C6.9375 5.1875 6.5 6.25 6.5 7.5C6.5 8.75 6.9375 9.8125 7.8125 10.6875C8.6875 11.5625 9.75 12 11 12ZM11 10.2C10.25 10.2 9.6125 9.9375 9.0875 9.4125C8.5625 8.8875 8.3 8.25 8.3 7.5C8.3 6.75 8.5625 6.1125 9.0875 5.5875C9.6125 5.0625 10.25 4.8 11 4.8C11.75 4.8 12.3875 5.0625 12.9125 5.5875C13.4375 6.1125 13.7 6.75 13.7 7.5C13.7 8.25 13.4375 8.8875 12.9125 9.4125C12.3875 9.9375 11.75 10.2 11 10.2ZM11 15C8.56667 15 6.35 14.3208 4.35 12.9625C2.35 11.6042 0.9 9.78333 0 7.5C0.9 5.21667 2.35 3.39583 4.35 2.0375C6.35 0.679167 8.56667 0 11 0C13.4333 0 15.65 0.679167 17.65 2.0375C19.65 3.39583 21.1 5.21667 22 7.5C21.1 9.78333 19.65 11.6042 17.65 12.9625C15.65 14.3208 13.4333 15 11 15ZM11 13C12.8833 13 14.6125 12.5042 16.1875 11.5125C17.7625 10.5208 18.9667 9.18333 19.8 7.5C18.9667 5.81667 17.7625 4.47917 16.1875 3.4875C14.6125 2.49583 12.8833 2 11 2C9.11667 2 7.3875 2.49583 5.8125 3.4875C4.2375 4.47917 3.03333 5.81667 2.2 7.5C3.03333 9.18333 4.2375 10.5208 5.8125 11.5125C7.3875 12.5042 9.11667 13 11 13Z"
                        fill="#474747"
                      />
                    </svg>
                    <span>View Details</span>
                  </button>
                )}
              </MenuItem>
              {/* <MenuItem>
                {() => (
                  <button
                    onClick={() => setIsDeleteModalOpen(true)}
                    className={`w-full text-left p-4 text-text-primary flex items-center gap-4 cursor-pointer hover:bg-gray-200 `}
                  >
                    <svg
                      width="16"
                      height="18"
                      viewBox="0 0 16 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3 18C2.45 18 1.97917 17.8042 1.5875 17.4125C1.19583 17.0208 1 16.55 1 16V3H0V1H5V0H11V1H16V3H15V16C15 16.55 14.8042 17.0208 14.4125 17.4125C14.0208 17.8042 13.55 18 13 18H3ZM13 3H3V16H13V3ZM5 14H7V5H5V14ZM9 14H11V5H9V14Z"
                        fill="#E03A3D"
                      />
                    </svg>

                    <span>Delete Annoucement</span>
                  </button>
                )}
              </MenuItem> */}
            </div>
          </MenuItems>
        </Transition>
      </Menu>

      {isDeleteModalOpen && (
        <DeleteAnnouncementModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={(id) => {
            // Handle delete confirmation
            handleDelete(id);
            setIsDeleteModalOpen(false);
          }}
          announcement={announcement}
        />
      )}
    </div>
  );
};
